/**
 * Form Submission Actions Service
 * 
 * Handles API calls for form submission actions including:
 * - Creating submission actions (addendum, invalidate, acknowledge)
 * - Fetching submission action history
 * - Getting form submissions for a specific patient
 */

import apiClient from './apiClient';

class FormSubmissionActionsService {
    constructor() {
        this.baseURL = '/dynamic-forms';
    }

    /**
     * Create a new submission action
     * @param {string} submissionId - The submission ID
     * @param {Object} actionData - Action data (action_type, reason, etc.)
     * @returns {Promise<Object>} API response
     */
    async createSubmissionAction(submissionId, actionData) {
        try {
            const response = await apiClient.post(
                `${this.baseURL}/submissions/${submissionId}/actions`,
                actionData
            );
            
            return {
                success: true,
                data: response.data,
                message: 'Action created successfully'
            };
        } catch (error) {
            console.error('Error creating submission action:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to create submission action',
                data: null
            };
        }
    }

    /**
     * Get all actions for a specific submission
     * @param {string} submissionId - The submission ID
     * @returns {Promise<Object>} API response with actions list
     */
    async getSubmissionActions(submissionId) {
        try {
            const response = await apiClient.get(
                `${this.baseURL}/submissions/${submissionId}/actions`
            );
            
            return {
                success: true,
                data: response.data,
                message: 'Actions retrieved successfully'
            };
        } catch (error) {
            console.error('Error fetching submission actions:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to fetch submission actions',
                data: []
            };
        }
    }

    /**
     * Get all form submissions for a specific patient
     * @param {string} formId - The form ID
     * @param {string} patientId - The patient ID
     * @returns {Promise<Object>} API response with submissions list
     */
    async getFormSubmissionsForPatient(formId, patientId) {
        try {
            const response = await apiClient.get(
                `${this.baseURL}/forms/${formId}/submissions/patient/${patientId}`
            );
            
            return {
                success: true,
                data: response.data,
                message: 'Submissions retrieved successfully'
            };
        } catch (error) {
            console.error('Error fetching form submissions for patient:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to fetch form submissions',
                data: []
            };
        }
    }

    /**
     * Get submission with its action history
     * @param {string} submissionId - The submission ID
     * @returns {Promise<Object>} API response with submission and actions
     */
    async getSubmissionWithActions(submissionId) {
        try {
            const [submissionResponse, actionsResponse] = await Promise.all([
                apiClient.get(`${this.baseURL}/submissions/${submissionId}`),
                this.getSubmissionActions(submissionId)
            ]);
            
            return {
                success: true,
                data: {
                    submission: submissionResponse.data,
                    actions: actionsResponse.data || []
                },
                message: 'Submission and actions retrieved successfully'
            };
        } catch (error) {
            console.error('Error fetching submission with actions:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to fetch submission details',
                data: null
            };
        }
    }

    /**
     * Check if a form allows specific actions
     * @param {string} formId - The form ID
     * @returns {Promise<Object>} API response with form action permissions
     */
    async getFormActionPermissions(formId) {
        try {
            const response = await apiClient.get(`${this.baseURL}/forms/${formId}`);
            const form = response.data;
            
            return {
                success: true,
                data: form.action_permissions || {
                    enable_template: true,
                    enable_invalidate: true,
                    enable_addendum: true,
                    enable_acknowledge: true,
                    late_entry_hours: 24,
                    require_reason_for_changes: true
                },
                message: 'Form permissions retrieved successfully'
            };
        } catch (error) {
            console.error('Error fetching form action permissions:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to fetch form permissions',
                data: null
            };
        }
    }

    /**
     * Validate if an action can be performed on a submission
     * @param {string} submissionId - The submission ID
     * @param {string} actionType - The action type (addendum, invalidate, acknowledge)
     * @returns {Promise<Object>} Validation result
     */
    async validateActionPermission(submissionId, actionType) {
        try {
            // This could be enhanced to call a specific validation endpoint
            // For now, we'll do client-side validation based on form permissions
            const submissionResponse = await apiClient.get(`${this.baseURL}/submissions/${submissionId}`);
            const submission = submissionResponse.data;
            
            const permissionsResponse = await this.getFormActionPermissions(submission.form_id);
            const permissions = permissionsResponse.data;
            
            const canPerformAction = {
                addendum: permissions.enable_addendum,
                invalidate: permissions.enable_invalidate,
                acknowledge: permissions.enable_acknowledge,
                template: permissions.enable_template
            }[actionType];
            
            return {
                success: true,
                data: {
                    canPerform: canPerformAction,
                    permissions: permissions,
                    submission: submission
                },
                message: canPerformAction ? 'Action is allowed' : 'Action is not permitted'
            };
        } catch (error) {
            console.error('Error validating action permission:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to validate action permission',
                data: null
            };
        }
    }

    /**
     * Get submission statistics for a form
     * @param {string} formId - The form ID
     * @returns {Promise<Object>} API response with submission statistics
     */
    async getSubmissionStats(formId) {
        try {
            const response = await apiClient.get(`${this.baseURL}/forms/${formId}/stats`);
            
            return {
                success: true,
                data: response.data,
                message: 'Statistics retrieved successfully'
            };
        } catch (error) {
            console.error('Error fetching submission statistics:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to fetch statistics',
                data: null
            };
        }
    }
}

// Create and export a singleton instance
const formSubmissionActionsService = new FormSubmissionActionsService();
export { formSubmissionActionsService };
export default formSubmissionActionsService;